/**
 * Log Retry Utility
 * Implements retry mechanism with exponential backoff
 * Following SOLID principles:
 * - Single Responsibility: Retry logic only
 * - Open/Closed: Extensible through configuration
 * 
 * @module logRetry
 */

import { getDefaultConfig } from './LoggingConstants';

/**
 * Retry Manager Class
 * Handles retry logic with exponential backoff
 * 
 * @class LogRetry
 */
class LogRetry {
    /**
     * Create a LogRetry instance
     * @param {Object} options - Optional configuration
     * @param {number} options.baseInterval - Base retry interval in ms (default: from constants)
     * @param {number} options.maxRetries - Maximum number of retries (default: from constants)
     * @param {number} options.backoffMultiplier - Exponential backoff multiplier (default: from constants)
     */
    constructor(options = {}) {
        this._baseIntervalOverride = options.baseInterval;
        this._maxRetriesOverride = options.maxRetries;
        this._backoffMultiplierOverride = options.backoffMultiplier;
    }

    get baseInterval() {
        const v = this._baseIntervalOverride !== undefined ? this._baseIntervalOverride : getDefaultConfig().RETRY_INTERVAL;
        const n = typeof v === 'number' ? v : parseInt(String(v), 10);
        if (n <= 0) throw new Error('baseInterval must be greater than 0');
        return n;
    }
    get maxRetries() {
        const v = this._maxRetriesOverride !== undefined ? this._maxRetriesOverride : getDefaultConfig().MAX_RETRIES;
        const n = typeof v === 'number' ? v : parseInt(String(v), 10);
        if (n < 0) throw new Error('maxRetries must be non-negative');
        return n;
    }

    /** Resolved at access time so runtime config is used. */
    get backoffMultiplier() {
        const v = this._backoffMultiplierOverride !== undefined ? this._backoffMultiplierOverride : getDefaultConfig().RETRY_BACKOFF_MULTIPLIER;
        const n = typeof v === 'number' ? v : parseFloat(String(v));
        if (n <= 1) throw new Error('backoffMultiplier must be greater than 1');
        return n;
    }

    /**
     * Calculate delay for retry attempt using exponential backoff
     * @param {number} attemptNumber - Current attempt number (0-indexed)
     * @returns {number} Delay in milliseconds
     */
    calculateDelay(attemptNumber) {
        if (typeof attemptNumber !== 'number' || attemptNumber < 0) {
            return this.baseInterval;
        }
        return this.baseInterval * Math.pow(this.backoffMultiplier, attemptNumber);
    }

    /**
     * Execute a function with retry logic
     * @param {Function} fn - Async function to execute
     * @param {Object} options - Retry options
     * @param {number} options.maxRetries - Maximum number of retries (default: this.maxRetries)
     * @param {Function} options.onRetry - Callback called on each retry (error, attemptNumber, delay) => void
     * @param {Function} options.shouldRetry - Function to determine if error should be retried (error, attemptNumber) => boolean
     * @returns {Promise<*>} Promise that resolves/rejects based on function outcome
     * @throws {Error} If fn is not a function
     */
    async executeWithRetry(fn, options = {}) {
        if (typeof fn !== 'function') {
            throw new Error('fn must be a function');
        }
        
        const {
            maxRetries = this.maxRetries,
            onRetry = null,
            shouldRetry = () => true
        } = options;

        // Validate maxRetries
        const validMaxRetries = Math.max(0, Math.floor(maxRetries));

        let lastError = null;

        for (let attempt = 0; attempt <= validMaxRetries; attempt++) {
            try {
                const result = await fn();
                return result;
            } catch (error) {
                lastError = error;

                // Check if we should retry this error
                const shouldRetryError = typeof shouldRetry === 'function' 
                    ? shouldRetry(error, attempt) 
                    : true;
                    
                if (!shouldRetryError) {
                    throw error;
                }

                // Don't retry on last attempt
                if (attempt >= validMaxRetries) {
                    break;
                }

                // Calculate delay and wait
                const delay = this.calculateDelay(attempt);
                
                if (typeof onRetry === 'function') {
                    try {
                        onRetry(error, attempt + 1, delay);
                    } catch (callbackError) {
                        // Don't let callback errors break retry logic
                        if (process.env.NODE_ENV === 'development') {
                            console.warn('Error in onRetry callback:', callbackError);
                        }
                    }
                }

                await this.sleep(delay);
            }
        }

        throw lastError;
    }

    /**
     * Sleep utility for delays
     * @param {number} ms - Milliseconds to sleep
     * @returns {Promise} Promise that resolves after delay
     */
    sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    /**
     * Update retry configuration
     * @param {Object} config - New configuration
     * @throws {Error} If configuration values are invalid
     */
    updateConfig(config) {
        if (!config || typeof config !== 'object') {
            throw new Error('config must be an object');
        }
        
        if (config.baseInterval !== undefined) {
            if (typeof config.baseInterval !== 'number' || config.baseInterval <= 0) {
                throw new Error('baseInterval must be a positive number');
            }
            this._baseIntervalOverride = config.baseInterval;
        }
        
        if (config.maxRetries !== undefined) {
            if (typeof config.maxRetries !== 'number' || config.maxRetries < 0) {
                throw new Error('maxRetries must be a non-negative number');
            }
            this._maxRetriesOverride = config.maxRetries;
        }
        
        if (config.backoffMultiplier !== undefined) {
            if (typeof config.backoffMultiplier !== 'number' || config.backoffMultiplier <= 1) {
                throw new Error('backoffMultiplier must be a number greater than 1');
            }
            this._backoffMultiplierOverride = config.backoffMultiplier;
        }
    }
}

// Export singleton instance
export const logRetry = new LogRetry();
export default logRetry;

